// Dialog leve de criação de template (§4.4 → §4.5): nome
// obrigatório + canal + white label (default: o WL selecionado no
// portal). POST /notification-center/templates → uuid REAL → navega
// para o editor já na v1 DRAFT (o cache de versões é semeado pela
// mutation com a resposta — sem o antigo sentinela "new").
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { useNotificationCenterWhiteLabels } from "../hooks"
import { useCreateTemplate } from "../template-editor-mutations"

const dialogPrefix =
  "pages.notificationCenter.templatesPage.createDialog"

// Canais do contrato (POST /templates aceita os 3).
const TEMPLATE_CHANNELS = [
  "EMAIL",
  "WHATSAPP",
  "SMS",
] as const

const buildCreateTemplateSchema = (
  requiredMessage: string
) =>
  z.object({
    name: z.string().trim().min(1, requiredMessage),
    channel: z.enum(TEMPLATE_CHANNELS),
    whiteLabelId: z.string().min(1, requiredMessage),
  })
type CreateTemplateFormValues = z.infer<
  ReturnType<typeof buildCreateTemplateSchema>
>

type TemplateCreateDialogProps = {
  // WL selecionado no portal — default do select.
  defaultWhiteLabelId?: string
  onClose: () => void
}

export const TemplateCreateDialog = ({
  defaultWhiteLabelId,
  onClose,
}: TemplateCreateDialogProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createTemplate = useCreateTemplate()
  // Catálogo de WLs (o dialog só monta aberto → 1 fetch).
  const { data: whiteLabels } =
    useNotificationCenterWhiteLabels()

  const form = useForm<CreateTemplateFormValues>({
    resolver: zodResolver(
      buildCreateTemplateSchema(
        t(`${dialogPrefix}.required`)
      )
    ),
    defaultValues: {
      name: "",
      channel: "EMAIL",
      whiteLabelId: defaultWhiteLabelId ?? "",
    },
  })

  const handleSubmit = form.handleSubmit((values) => {
    createTemplate.mutate(values, {
      // Cache de versões já semeado pela mutation — navega para o
      // editor com o uuid REAL devolvido pelo backend.
      onSuccess: (result) => {
        onClose()
        navigate(
          `/notification-center/templates/${result.uuid}`
        )
      },
    })
  })

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`${dialogPrefix}.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${dialogPrefix}.description`)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${dialogPrefix}.name`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t(
                        `${dialogPrefix}.namePlaceholder`
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="channel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${dialogPrefix}.channel`)}
                  </FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger
                        aria-label={t(
                          `${dialogPrefix}.channel`
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {TEMPLATE_CHANNELS.map((channel) => (
                        <SelectItem
                          key={channel}
                          value={channel}
                        >
                          {channel}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="whiteLabelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${dialogPrefix}.whiteLabel`)}
                  </FormLabel>
                  <Select
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger
                        aria-label={t(
                          `${dialogPrefix}.whiteLabel`
                        )}
                      >
                        <SelectValue
                          placeholder={t(
                            `${dialogPrefix}.whiteLabelPlaceholder`
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {/* Garante a opção do WL selecionado mesmo
                          antes do catálogo carregar. */}
                      {defaultWhiteLabelId &&
                        !whiteLabels?.some(
                          (whiteLabel) =>
                            whiteLabel.id ===
                            defaultWhiteLabelId
                        ) && (
                          <SelectItem
                            value={defaultWhiteLabelId}
                          >
                            {defaultWhiteLabelId}
                          </SelectItem>
                        )}
                      {whiteLabels?.map((whiteLabel) => (
                        <SelectItem
                          key={whiteLabel.id}
                          value={whiteLabel.id}
                        >
                          {whiteLabel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {t(`${dialogPrefix}.cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={createTemplate.isPending}
              >
                {t(`${dialogPrefix}.create`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
